"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.rotateAboutZ = rotateAboutZ;
exports.headInLift = headInLift;
exports.launchPoint = launchPoint;
exports.release = release;
exports.strokeRelease = strokeRelease;
/**
 * Trainer kinematics: where the ball leaves the trainer, and how it is moving
 * and spinning when it does.
 *
 * The arithmetic follows v1's TrainerSim step for step, so results are
 * bit-identical to it; where two expressions look interchangeable but round
 * differently (degrees to radians, for one), the one v1 uses is kept.
 */
const types_1 = require("./types");
const DEG_TO_RAD = Math.PI / 180.0;
function rotateAboutX(v, degrees) {
    if (degrees === 0) {
        return v;
    }
    const c = Math.cos(degrees * DEG_TO_RAD);
    const s = Math.sin(degrees * DEG_TO_RAD);
    return { x: v.x, y: v.y * c - v.z * s, z: v.y * s + v.z * c };
}
/** v rotated `degrees` about +z, counterclockwise seen from above. */
function rotateAboutZ(v, degrees) {
    if (degrees === 0) {
        return v;
    }
    const c = Math.cos(degrees * DEG_TO_RAD);
    const s = Math.sin(degrees * DEG_TO_RAD);
    return { x: v.x * c - v.y * s, y: v.x * s + v.y * c, z: v.z };
}
/**
 * The spin axis before any head rotation: in the x-z plane at `axis` degrees,
 * reversed for positive spin so the Magnus force falls out of a cross product.
 */
function unrotatedSpinAxis(spin) {
    const x = Math.cos((spin.axis * Math.PI) / 180.0);
    const z = Math.sin((spin.axis * Math.PI) / 180.0);
    return spin.rpm > 0 ? { x: -x, y: -0, z: -z } : { x, y: 0, z };
}
/**
 * The end of the lift arm at a head height, in lift coordinates: the arm
 * swings in the trainer's y-z plane about the pivot, and lift coordinates put
 * the origin at the arm's tip when it is level.
 *
 * Throws on a head height the lift arm cannot reach.
 */
function headInLift(geometry, headHeight) {
    const { liftPivot, liftArmLength: arm } = geometry;
    const headZ = headHeight - liftPivot.z;
    if (!(Math.abs(headZ) <= arm)) {
        throw new RangeError(`head height ${headHeight} is out of the lift arm's reach [${liftPivot.z - arm}, ${liftPivot.z + arm}]`);
    }
    return { x: 0, y: -arm + Math.sqrt(arm * arm - headZ * headZ), z: headZ };
}
/**
 * Where the ball leaves the wheels, in court coordinates, for a trainer at
 * `pose` with its head tilted `pitch` and panned `yaw` degrees.
 *
 * Throws on angles beyond ±90° or a head height the lift arm cannot reach.
 */
function launchPoint(geometry, pose, pitch = 0, yaw = 0) {
    if (!(Math.abs(yaw) <= 90 && Math.abs(pitch) <= 90)) {
        throw new RangeError(`head yaw and pitch (${yaw}, ${pitch}) must be in [-90, 90]`);
    }
    const { liftPivot, liftArmLength: arm, launchOriginOffset: offset, launchRadius } = geometry;
    const head = headInLift(geometry, pose.headHeight);
    // The wheels rotate about a point fixed to the head; the ball leaves
    // launchRadius in front of it, along the head's aim.
    const reach = launchRadius * Math.cos(pitch * DEG_TO_RAD);
    const lift = {
        x: head.x + offset.x + reach * Math.sin(yaw * DEG_TO_RAD),
        y: head.y + offset.y + reach * Math.cos(yaw * DEG_TO_RAD),
        z: head.z + offset.z + launchRadius * Math.sin(pitch * DEG_TO_RAD),
    };
    const body = {
        x: lift.x + liftPivot.x,
        y: lift.y + (liftPivot.y + arm),
        z: lift.z + liftPivot.z,
    };
    const turned = rotateAboutZ(body, -pose.yaw);
    return { x: turned.x + pose.x, y: turned.y + pose.y, z: turned.z };
}
/**
 * The release for a trainer at `pose` throwing `launch`. With the "v1" spin
 * model it is bit-identical to v1's _initializeLaunch.
 */
function release(profile, pose, launch, spinModel = "physical") {
    const { geometry, mechanism } = profile.trainer;
    const { speed, spin } = launch;
    const origin = launchPoint(geometry, pose, launch.pitch, launch.yaw);
    const correction = mechanism.deflectionCorrection(spin, speed);
    const yaw = launch.yaw - correction.yaw;
    const pitch = launch.pitch - correction.pitch;
    const heading = ((pose.yaw + yaw) * Math.PI) / 180.0;
    const elevation = (pitch * Math.PI) / 180.0;
    const horizontal = speed * Math.cos(elevation);
    // The axis is given looking at the ball from behind (x right, z up), with
    // the ball flying +y. "physical" turns it exactly as the ball's direction
    // turns: tilt about x, then pan and body yaw about z, so it stays
    // perpendicular to the throw.
    const spinAxis = spinModel === "physical"
        ? rotateAboutZ(rotateAboutX(unrotatedSpinAxis(spin), pitch), -(pose.yaw + yaw))
        : rotateAboutZ(rotateAboutX(rotateAboutZ(unrotatedSpinAxis(spin), -yaw), -pitch), -pose.yaw);
    return {
        origin,
        velocity: {
            x: horizontal * Math.sin(heading),
            y: horizontal * Math.cos(heading),
            z: speed * Math.sin(elevation),
        },
        spinAxis,
        spinRpm: spin.rpm,
        pitch,
        yaw,
        deflection: { pitch: -correction.pitch, yaw: -correction.yaw },
    };
}
/**
 * The release for a ball struck by a player: no head and no deflection.
 *
 * "v1" leaves the spin axis unrotated in the court's x-z plane, bit-identical
 * to v1's _initializeStroke, so its topspin is topspin only for a ball flying
 * +y. "physical" reads the axis as for a trainer throw, seen from behind the
 * ball, and turns it with the stroke's own elevation and heading as release()
 * turns a throw's: topspin dips whichever way the ball is struck.
 */
function strokeRelease(origin, velocity, spin = types_1.NO_SPIN, spinModel = "physical") {
    return {
        origin: { x: origin.x, y: origin.y, z: origin.z },
        velocity: { x: velocity.x, y: velocity.y, z: velocity.z },
        spinAxis: spinModel === "physical" ? alongStroke(unrotatedSpinAxis(spin), velocity) : unrotatedSpinAxis(spin),
        spinRpm: spin.rpm,
        pitch: 0,
        yaw: 0,
        deflection: { pitch: 0, yaw: 0 },
    };
}
/** An axis given for a ball flying +y, turned to fly along `velocity`: tilt by its elevation, then pan by its heading. */
function alongStroke(axis, velocity) {
    const heading = (Math.atan2(velocity.x, velocity.y) * 180.0) / Math.PI;
    const elevation = (Math.atan2(velocity.z, Math.hypot(velocity.x, velocity.y)) * 180.0) / Math.PI;
    return rotateAboutZ(rotateAboutX(axis, elevation), -heading);
}
